/*
 * Language + country/currency selector.
 *
 * The script carries no mapping of its own. Each preset option already knows
 * the country it resolves to (data-country, written by Liquid from the single
 * table in snippets/lts-language-selector.liquid), so this file only has to
 * copy values into the two hidden inputs and submit the form once.
 *
 * Every lookup is scoped to the instance's own root element. The header and the
 * drawer each render the snippet, so a document-wide querySelector would find
 * the wrong form and update only one of the two hidden inputs.
 *
 * Progressive enhancement: the panel starts hidden and is only wired up here.
 * A <noscript> block in the snippet renders plain selects for browsers without
 * scripting, so nothing is unreachable.
 */
(function () {
  'use strict';

  var INSTANCE_FLAG = 'ltsLangReady';

  function Selector(root) {
    this.root = root;
    this.form = root.querySelector('[data-lts-lang-form]');
    this.trigger = root.querySelector('[data-lts-lang-trigger]');
    this.panel = root.querySelector('[data-lts-lang-panel]');
    this.list = root.querySelector('[data-lts-lang-list]');
    this.langInput = root.querySelector('[data-lts-lang-input]');
    this.countryInput = root.querySelector('[data-lts-country-input]');
    this.countrySelect = root.querySelector('[data-lts-country-select]');
    this.applyBtn = root.querySelector('[data-lts-country-apply]');
    /* Sheet-only parts. Their absence is what marks this as the popover. */
    this.backdrop = root.querySelector('[data-lts-lang-backdrop]');
    this.closeBtn = root.querySelector('[data-lts-lang-close]');
    this.isSheet = !!this.backdrop;
    this.options = Array.prototype.slice.call(root.querySelectorAll('[data-lts-lang-option]'))
      .filter(function (o) { return !o.disabled; });
    this.submitted = false;
    this.index = -1;
  }

  Selector.prototype.isOpen = function () {
    return this.trigger.getAttribute('aria-expanded') === 'true';
  };

  Selector.prototype.open = function () {
    if (!this.panel || this.isOpen()) return;
    if (this.backdrop) this.backdrop.hidden = false;
    this.panel.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'true');
    /* Next frame, so the sheet transitions up from its off-screen resting
       position instead of appearing already in place. */
    if (this.isSheet) {
      var self = this;
      requestAnimationFrame(function () { self.root.classList.add('is-open'); });
      document.body.classList.add('lts-lang-locked');
    } else {
      this.root.classList.add('is-open');
    }
    this.clampToViewport();
    var selected = this.options.filter(function (o) {
      return o.getAttribute('aria-selected') === 'true';
    })[0];
    if (this.isSheet && !selected && this.closeBtn) {
      /* Nothing is current - land on the close button rather than implying the
         first row is the active one. */
      this.closeBtn.focus();
      return;
    }
    this.focusOption(selected ? this.options.indexOf(selected) : 0);
  };

  Selector.prototype.close = function (returnFocus) {
    if (!this.panel || !this.isOpen()) return;
    this.trigger.setAttribute('aria-expanded', 'false');
    this.root.classList.remove('is-open');
    this.panel.style.removeProperty('--lts-lang-shift');
    this.index = -1;

    var self = this;
    var finish = function () {
      self.panel.hidden = true;
      if (self.backdrop) self.backdrop.hidden = true;
    };
    if (this.isSheet) {
      document.body.classList.remove('lts-lang-locked');
      /* Let the slide-down finish before it is pulled from the flow. */
      var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduced) finish(); else setTimeout(finish, 220);
    } else {
      finish();
    }
    if (returnFocus) this.trigger.focus();
  };

  /* Keep the panel inside the window. The header instance sits near the right
     edge, and on a narrow desktop the panel would otherwise hang off-screen. */
  Selector.prototype.clampToViewport = function () {
    /* The sheet is fixed and full width; there is nothing to clamp. */
    if (!this.panel || this.isSheet) return;
    this.panel.style.removeProperty('--lts-lang-shift');
    var box = this.panel.getBoundingClientRect();
    var margin = 12;
    var overflowRight = box.right - (window.innerWidth - margin);
    var shift = 0;
    if (overflowRight > 0) shift = -overflowRight;
    if (box.left + shift < margin) shift = margin - box.left;
    if (shift) this.panel.style.setProperty('--lts-lang-shift', Math.round(shift) + 'px');
  };

  /* Everything focusable inside the sheet, in document order. */
  Selector.prototype.focusables = function () {
    return Array.prototype.slice.call(this.panel.querySelectorAll(
      'button:not([disabled]), select, a[href], input, [tabindex]:not([tabindex="-1"])'
    )).filter(function (el) { return el.offsetParent !== null; });
  };

  Selector.prototype.trapTab = function (e) {
    var items = this.focusables();
    if (!items.length) return;
    var first = items[0], last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  Selector.prototype.focusOption = function (i) {
    if (!this.options.length) return;
    if (i < 0) i = this.options.length - 1;
    if (i >= this.options.length) i = 0;
    this.index = i;
    this.options[i].focus();
  };

  /* One submit, ever. A double click or an Enter landing on an already
     submitting form must not post twice. */
  Selector.prototype.submitOnce = function () {
    if (this.submitted) return false;
    this.submitted = true;
    this.root.classList.add('is-submitting');
    if (this.form.requestSubmit) this.form.requestSubmit();
    else this.form.submit();
    return true;
  };

  Selector.prototype.chooseLanguage = function (option, event) {
    if (!option || option.disabled) return;
    if (event) event.preventDefault();
    var lang = option.getAttribute('data-language');
    var country = option.getAttribute('data-country');
    if (this.langInput && lang) this.langInput.value = lang;
    /* No data-country means the mapped market is not active. The language is
       still switched; the country is left exactly as it is rather than posting
       a code Shopify would reject. */
    if (this.countryInput && country) this.countryInput.value = country;
    this.submitOnce();
  };

  Selector.prototype.applyCountry = function (event) {
    if (!this.countrySelect || !this.countryInput) return;
    if (event) event.preventDefault();
    this.countryInput.value = this.countrySelect.value;
    /* Language deliberately untouched: this control changes region only. */
    this.submitOnce();
  };

  Selector.prototype.init = function () {
    var self = this;
    if (!this.form || !this.trigger || !this.panel) return;

    this.trigger.addEventListener('click', function (e) {
      e.preventDefault();
      if (self.isOpen()) self.close(true); else self.open();
    });

    this.trigger.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown' || e.key === 'Down') { e.preventDefault(); self.open(); }
    });

    this.options.forEach(function (option, i) {
      option.addEventListener('click', function (e) { self.chooseLanguage(option, e); });
      option.addEventListener('keydown', function (e) {
        switch (e.key) {
          case 'ArrowDown': case 'Down':  e.preventDefault(); self.focusOption(i + 1); break;
          case 'ArrowUp':   case 'Up':    e.preventDefault(); self.focusOption(i - 1); break;
          case 'Home':                    e.preventDefault(); self.focusOption(0); break;
          case 'End':                     e.preventDefault(); self.focusOption(self.options.length - 1); break;
          /* Enter and Space are the button's own activation; letting them
             through would submit the form before the inputs were set. */
          case 'Enter': case ' ': case 'Spacebar': self.chooseLanguage(option, e); break;
          case 'Escape': case 'Esc': e.preventDefault(); self.close(true); break;
          /* In the sheet Tab cycles inside the dialog; in the popover it moves
             on and closes, which is the expected popover behaviour. */
          case 'Tab':
            if (self.isSheet) self.trapTab(e); else self.close(false);
            break;
        }
      });
    });

    if (this.backdrop) {
      this.backdrop.addEventListener('click', function () { self.close(true); });
    }
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', function (e) { e.preventDefault(); self.close(true); });
      this.closeBtn.addEventListener('keydown', function (e) {
        if (e.key === 'Tab' && self.isSheet) self.trapTab(e);
      });
    }
    if (this.applyBtn) {
      this.applyBtn.addEventListener('click', function (e) { self.applyCountry(e); });
    }
    if (this.countrySelect) {
      this.countrySelect.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' || e.key === 'Esc') { e.preventDefault(); self.close(true); }
      });
    }

    this.panel.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' || e.key === 'Esc') { e.preventDefault(); self.close(true); }
      else if (e.key === 'Tab' && self.isSheet) self.trapTab(e);
    });

    /* Outside click and focus loss. Registered on the instance, and removed
       with it when the section is re-rendered, so nothing accumulates. */
    this._onDocPointer = function (e) {
      if (self.isSheet) return;
      if (self.isOpen() && !self.root.contains(e.target)) self.close(false);
    };
    this._onFocusIn = function (e) {
      if (self.isOpen() && !self.root.contains(e.target)) {
        /* Modal: pull focus back rather than closing. */
        if (self.isSheet) { var f = self.focusables(); if (f.length) f[0].focus(); return; }
        self.close(false);
      }
    };
    document.addEventListener('click', this._onDocPointer);
    document.addEventListener('focusin', this._onFocusIn);

    this.root[INSTANCE_FLAG] = this;
  };

  Selector.prototype.destroy = function () {
    document.removeEventListener('click', this._onDocPointer);
    document.removeEventListener('focusin', this._onFocusIn);
    /* Never leave the page unscrollable behind a removed sheet. */
    document.body.classList.remove('lts-lang-locked');
    this.root[INSTANCE_FLAG] = null;
  };

  /* Idempotent: safe to call again after a Section Rendering API swap or a
     theme-editor section load, and it never double-binds an existing root. */
  function init(scope) {
    var roots = (scope || document).querySelectorAll('[data-lts-lang]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root[INSTANCE_FLAG]) return;
      new Selector(root).init();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    var roots = e.target.querySelectorAll('[data-lts-lang]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root[INSTANCE_FLAG]) root[INSTANCE_FLAG].destroy();
    });
  });

  /* Sections swapped in by the Section Rendering API do not fire the theme
     editor events, so pick those up too.

     This used to call init() - a document-wide querySelectorAll - on every
     batch of DOM insertions. A storefront inserts nodes constantly (lazy
     images, sliders, popups, app widgets), so that ran hundreds of times and
     showed up as ~430 ms of scripting in a mobile trace. It now looks only
     inside the nodes that were actually added, and runs init() at most once
     per frame when one of them is, or contains, a selector. */
  if (window.MutationObserver) {
    var initQueued = false;
    new MutationObserver(function (records) {
      if (initQueued) return;
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.hasAttribute('data-lts-lang') || node.querySelector('[data-lts-lang]')) {
            initQueued = true;
            requestAnimationFrame(function () { initQueued = false; init(); });
            return;
          }
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
